import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  CloseButton,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from "@chakra-ui/react";
import React, { useContext, useEffect, useState } from "react";
import {
  UseFieldArrayAppend,
  UseFieldArrayMove,
  UseFieldArrayRemove,
} from "react-hook-form";

import { Link } from "../../../components";
import { useIsSmallScreen } from "../../../hooks/useIsSmallScreen";
import {
  AiInterviewerFragment,
  AiInterviewerVideoMode,
  AiInterviewQuestion,
} from "../../graphql";
import useFeatureFlag, {
  useFeatureFlagForCurrentOrImpersonatedUser,
} from "../../graphql/hooks/useFeatureFlag";
import useCurrentUser from "../../hooks/useCurrentUser";
import { useAgenticIntakeEnabled } from "../AiInterviewerWizard/AgenticIntake/useAgenticIntakeEnabled";
import { AiInterviewerWizardContext } from "../AiInterviewerWizard/AiInterviewerWizardContext";
import { AiInterviewerCandidates } from "./AiInterviewerCandidates";
import AiInterviewerGuide from "./AiInterviewerGuide";
import { AiInterviewerInsights } from "./AiInterviewerInsights/AiInterviewerInsights";
import AiInterviewerMobileContent from "./AiInterviewerMobileContent";
import AiInterviewerNotifications from "./AiInterviewerNotifications";
import AiInterviewerSettings from "./AiInterviewerSettings";
import { AiInterviewerTestInterviews } from "./AiInterviewerTestInterviews";
import { AiInterviewerFormValues, AiInterviewerVideoSetting } from "./types";
import { useAiInterviewerFormContext } from "./useAiInterviewerFormContext";
import useInterviewerTabs from "./useInterviewerTabs";

interface AiInterviewerContentProps {
  aiInterviewer: AiInterviewerFragment;
  readOnly?: boolean;
  questions: AiInterviewQuestion[];
  appendQuestion: UseFieldArrayAppend<AiInterviewerFormValues, "questions">;
  removeQuestion: UseFieldArrayRemove;
  move: UseFieldArrayMove;
  pendingAcceptedSuggestionIds: ReadonlySet<string>;
  onAcceptSuggestion: (suggestionId: string) => void;
}

const StyledTab: typeof Tab = (props) => <Tab pb="2.5" mb="0" {...props} />;

const AiInterviewerContent: React.FC<AiInterviewerContentProps> = ({
  aiInterviewer,
  readOnly,
  questions,
  appendQuestion,
  removeQuestion,
  move,
  pendingAcceptedSuggestionIds,
  onAcceptSuggestion,
}) => {
  const isSmallScreen = useIsSmallScreen();
  const isSuggestionsV2FeatureEnabled =
    useFeatureFlagForCurrentOrImpersonatedUser(
      "ai_interviewer:suggestionsV2:frontend"
    );
  const showVideoExperience = useFeatureFlag("ai_interviewer:video_experience");
  const insightsEnabled = useFeatureFlag("ai_interviewer:insights");
  const currentUser = useCurrentUser();
  const { watch, setValue } = useAiInterviewerFormContext();
  const { activeTab, handleChangeTab } = useInterviewerTabs({
    aiInterviewerId: aiInterviewer.id,
    insightsEnabled,
  });
  const orgVideoMode = currentUser.organization.aiInterviewerVideoMode;
  const orgVideoSetting: AiInterviewerVideoSetting =
    orgVideoMode ?? AiInterviewerVideoMode.Disabled;
  const orgDefaultsLabel = orgVideoSetting.toLowerCase();
  const interviewerVideoSetting = watch("videoSetting");
  const [isVideoSettingsAlertDismissed, setIsVideoSettingsAlertDismissed] =
    useState(false);
  useEffect(() => {
    setIsVideoSettingsAlertDismissed(false);
  }, [interviewerVideoSetting, orgVideoSetting]);
  const hasVideoSettingsMismatch =
    showVideoExperience &&
    interviewerVideoSetting !== orgVideoSetting &&
    !isVideoSettingsAlertDismissed;
  const isSettingsTabActive = activeTab === 2;

  const agenticIntakeEnabled = useAgenticIntakeEnabled();

  // Register tab setter with wizard context
  const { registerSetActiveTab, wizardOn } = useContext(
    AiInterviewerWizardContext
  );
  useEffect(() => {
    registerSetActiveTab?.(handleChangeTab);
  }, [registerSetActiveTab, handleChangeTab]);

  const hideTabList = wizardOn && agenticIntakeEnabled;
  const stickyTabListBg = "white";

  if (isSmallScreen) {
    return <AiInterviewerMobileContent aiInterviewer={aiInterviewer} />;
  }

  return (
    <Tabs
      isLazy
      index={activeTab}
      onChange={handleChangeTab}
      display="flex"
      flexDirection="column"
      flex={isSuggestionsV2FeatureEnabled ? undefined : "1"}
      minH={isSuggestionsV2FeatureEnabled ? undefined : "0"}
    >
      <Box
        position={isSuggestionsV2FeatureEnabled ? "sticky" : undefined}
        top={isSuggestionsV2FeatureEnabled ? 0 : undefined}
        zIndex={isSuggestionsV2FeatureEnabled ? 2 : undefined}
        bg={isSuggestionsV2FeatureEnabled ? stickyTabListBg : undefined}
        pt={isSuggestionsV2FeatureEnabled ? "4" : undefined}
        display={hideTabList ? "none" : undefined}
      >
        <TabList borderBottom="1px solid" borderColor="gray.200" bg="inherit">
          <StyledTab>Candidates</StyledTab>
          <StyledTab>Guide</StyledTab>
          <StyledTab>{agenticIntakeEnabled ? "Options" : "Settings"}</StyledTab>
          <StyledTab>Notifications</StyledTab>
          <StyledTab>Test interviews</StyledTab>
          {insightsEnabled && <StyledTab>Insights</StyledTab>}
        </TabList>
      </Box>
      {hasVideoSettingsMismatch && isSettingsTabActive && (
        <Alert
          status="warning"
          mt={4}
          borderRadius="md"
          position="relative"
          pr={10}
        >
          <AlertIcon />
          <AlertDescription>
            <Text as="span">
              This interviewer has different video settings than the org default
              ({orgDefaultsLabel}). Click{" "}
            </Text>
            <Link
              onClick={() => {
                setValue("videoSetting", orgVideoSetting, {
                  shouldDirty: true,
                });
              }}
            >
              here
            </Link>
            <Text as="span"> to set them as the org&apos;s.</Text>
          </AlertDescription>
          <CloseButton
            position="absolute"
            top={2}
            right={2}
            onClick={() => setIsVideoSettingsAlertDismissed(true)}
          />
        </Alert>
      )}

      <TabPanels
        display={isSuggestionsV2FeatureEnabled ? undefined : "flex"}
        flexDirection={isSuggestionsV2FeatureEnabled ? undefined : "column"}
        flex={isSuggestionsV2FeatureEnabled ? undefined : "1"}
        minH={isSuggestionsV2FeatureEnabled ? undefined : "0"}
      >
        <TabPanel
          data-testid="ai-interviewer-candidates-panel"
          display="flex"
          flex="1"
          minH="0"
        >
          <AiInterviewerCandidates aiInterviewer={aiInterviewer} />
        </TabPanel>
        <TabPanel data-testid="ai-interviewer-guide-panel" pb={4}>
          <AiInterviewerGuide
            readOnly={readOnly ?? false}
            questions={questions}
            appendQuestion={appendQuestion}
            removeQuestion={removeQuestion}
            move={move}
            pendingAcceptedSuggestionIds={pendingAcceptedSuggestionIds}
            onAcceptSuggestion={onAcceptSuggestion}
          />
        </TabPanel>
        <TabPanel data-testid="ai-interviewer-settings-panel">
          <AiInterviewerSettings
            readOnly={readOnly ?? false}
            aiInterviewer={aiInterviewer}
          />
        </TabPanel>
        <TabPanel data-testid="ai-interviewer-notifications-panel">
          <AiInterviewerNotifications
            aiInterviewer={aiInterviewer}
            readOnly={readOnly ?? false}
          />
        </TabPanel>
        <TabPanel
          data-testid="ai-interviewer-test-interview-panel"
          display="flex"
          flex="1"
          minH="0"
        >
          <AiInterviewerTestInterviews aiInterviewer={aiInterviewer} />
        </TabPanel>
        {insightsEnabled && (
          <TabPanel data-testid="ai-interviewer-insights-panel">
            <AiInterviewerInsights aiInterviewerId={aiInterviewer.id} />
          </TabPanel>
        )}
      </TabPanels>
    </Tabs>
  );
};

export default AiInterviewerContent;
